import React from 'react';
import { useImmerReducer } from 'use-immer';
import initialStates, { RADIOS_INITIAL_VALUES } from '../lib/buildInitialState';
import { Box, Flex, Heading, HStack, Input, Switch, Text, Textarea, Field } from '@chakra-ui/react';
import theme from '../theme';
import SignaturePad from '../components/SignaturePad';

function reducer(draft, action) {
	switch (action.type) {
		case 'initialize_fields': {
			draft.fields = initialStates.hazardous_energy_loto;
			break;
		}
		case 'update_field_value': {
			const { field, key, value } = action.payload;

			if (key === 'yes' || key === 'no') {
				draft.fields[field] = {
					...draft.fields[field],
					...RADIOS_INITIAL_VALUES,
					[key]: value
				};
			} else {
				draft.fields[field] = value;
			}
			break;
		}
		case 'update_concern_field': {
			const { section, concern, key, value } = action.payload;

			if (key === 'yes' || key === 'no') {
				// If yes/no only,
				// preserve existing verified/other values,
				// override yes/no inputs,
				// set yes/no value
				draft.fields.concerns[section].fields[concern] = {
					...draft.fields.concerns[section].fields[concern],
					...RADIOS_INITIAL_VALUES,
					[key]: value
				};
			} else {
				// otherwise, don't affect the yes/no values,
				// only set the values of fields not related to yes/no
				draft.fields.concerns[section].fields[concern][key] = value;
			}
			break;
		}
		case 'save_signature': {
			const { signatureData, signatureType } = action.payload;
			draft.fields[`${signatureType}_signature`] = signatureData;
			break;
		}
		default:
			break;
	}
}

export default ({ formData, saveForm, currentPacket, currentForm, isUnassigned, isSubmitted, edit }) => {
	const [state, dispatch] = useImmerReducer(reducer, { fields: formData });

	React.useEffect(() => {
		if (Array.isArray(state.fields)) {
			dispatch({ type: 'initialize_fields' });
		}
	}, []);

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.fields
		});
	}, [state.fields]);

	return (
		<>
			{currentPacket.packet_tag === 'supervisor' || isUnassigned ? (
				<Flex>
					<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Flex flexDirection="row" alignItems="center">
							<Flex px="8px" justifyContent="center" alignItems="center">
								<Flex
									justifyContent="center"
									alignItems="center"
									w="24px"
									h="24px"
									borderWidth="2px"
									borderRadius="50%"
									// Null propagation for retroactively added fields
									borderColor={state.fields?.lockout_tagout_required?.yes ? 'green' : 'transparent'}
									_hover={{ cursor: isSubmitted && !edit ? 'default' : 'pointer' }}
									pointerEvents={isSubmitted && !edit ? 'none' : 'auto'}
									onClick={() => {
										if (!(isSubmitted && !edit)) {
											dispatch({
												type: 'update_field_value',
												payload: {
													field: 'lockout_tagout_required',
													key: 'yes',
													value: !state.fields?.lockout_tagout_required?.yes
												}
											});
										}
									}}
								>
									<Text fontSize={18} w="17.5px" align="center">
										Y
									</Text>
								</Flex>
							</Flex>
							<Flex px="8px" justifyContent="center" alignItems="center">
								<Flex
									justifyContent="center"
									alignItems="center"
									w="24px"
									h="24px"
									borderWidth="2px"
									borderRadius="50%"
									borderColor={state.fields?.lockout_tagout_required?.no ? 'red' : 'transparent'}
									_hover={{ cursor: isSubmitted && !edit ? 'default' : 'pointer' }}
									pointerEvents={isSubmitted && !edit ? 'none' : 'auto'}
									onClick={() =>
										dispatch({
											type: 'update_field_value',
											payload: {
												field: 'lockout_tagout_required',
												key: 'no',
												value: !state.fields?.lockout_tagout_required?.no
											}
										})
									}
								>
									<Text fontSize={18} w="17.5px" align="center">
										N
									</Text>
								</Flex>
							</Flex>
						</Flex>
						<Text fontSize={16} as="label" pl="4px">
							Does this job require any Lockout/Tagout?
						</Text>
					</HStack>
				</Flex>
			) : null}
			<Flex>
				<Flex flexDirection="row" alignItems="stretch" justifyContent="space-between" wrap="wrap">
					{Object.keys(state.fields?.concerns || {}).map((concernSection, index) => (
						<Box
							key={`${concernSection}_${index}`}
							flex={1}
							flexBasis="48%"
							p="8px"
							m="8px"
							bgColor={theme.colors.gray[500]}
							borderRadius="base"
						>
							<HStack pb="8px">
								<Text fontSize={16} flexGrow={1} flexShrink={1} flexBasis="75%" as="label" pb="4px">
									{state.fields?.concerns?.[concernSection]?.title}
								</Text>
								<Flex
									flexGrow={1}
									flexShrink={1}
									flexBasis="25%"
									ml="2px"
									alignItems="center"
									justifyContent="space-between"
								>
									<Text fontSize={16} whiteSpace="nowrap" textAlign="center">
										YES
									</Text>
									<Text fontSize={16} whiteSpace="nowrap" textAlign="center">
										NO
									</Text>
									<Text fontSize={16} whiteSpace="nowrap" textAlign="center">
										VERIFIED
									</Text>
								</Flex>
							</HStack>
							{Object.keys(state.fields?.concerns?.[concernSection]?.fields).map((concern, _index) => (
								<Flex
									key={`${concern}_${_index}`}
									flexDirection="row"
									alignItems="center"
									justifyContent="space-between"
									mb="10px"
								>
									{concern === 'other' ? (
										<Flex flexGrow={1} flexShrink={1} flexBasis="75%">
											<Box
												w="100%"
												mr="10px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text fontSize={16} as="label" pb="4px">
													Other
												</Text>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={
														state.fields?.concerns?.[concernSection]?.fields?.[concern]
															?.other
													}
													onChange={({ target }) =>
														dispatch({
															type: 'update_concern_field',
															payload: {
																section: concernSection,
																concern,
																key: 'other',
																value: target.value
															}
														})
													}
													disabled={
														!edit &&
														(isSubmitted ||
															(currentPacket.packet_tag !== 'supervisor' &&
																!isUnassigned))
													}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Box>
										</Flex>
									) : (
										<Text fontSize={16} flexGrow={1} flexShrink={1} flexBasis="75%">
											{state.fields?.concerns?.[concernSection]?.fields?.[concern]?.title}
										</Text>
									)}
									<Flex
										flexGrow={1}
										flexShrink={1}
										flexBasis="25%"
										justifyContent="space-between"
										alignItems="center"
										ml="2px"
									>
										<Box
											w="20px"
											h="20px"
											borderWidth="2px"
											borderRadius="50%"
											borderColor={theme.colors.blue[700]}
											bgColor={
												state.fields?.concerns?.[concernSection]?.fields?.[concern]?.yes
													? theme.colors.blue[700]
													: 'transparent'
											}
											_hover={{
												cursor:
													currentPacket.packet_tag !== 'supervisor' && !isUnassigned && !edit
														? 'default'
														: 'pointer'
											}}
											pointerEvents={
												currentPacket.packet_tag !== 'supervisor' && !isUnassigned && !edit
													? 'none'
													: 'auto'
											}
											onClick={() => {
												if (
													edit ||
													!(currentPacket.packet_tag !== 'supervisor' && !isUnassigned)
												) {
													dispatch({
														type: 'update_concern_field',
														payload: {
															section: concernSection,
															concern,
															key: 'yes',
															value: !state.fields?.concerns?.[concernSection]?.fields?.[
																concern
															]?.yes
														}
													});
												}
											}}
										/>
										<Box
											w="20px"
											h="20px"
											borderWidth="2px"
											borderRadius="50%"
											borderColor={theme.colors.blue[700]}
											bgColor={
												state.fields?.concerns?.[concernSection]?.fields?.[concern]?.no
													? theme.colors.blue[700]
													: 'transparent'
											}
											_hover={{
												cursor:
													currentPacket.packet_tag !== 'supervisor' && !isUnassigned && !edit
														? 'default'
														: 'pointer'
											}}
											pointerEvents={
												currentPacket.packet_tag !== 'supervisor' && !isUnassigned && !edit
													? 'none'
													: 'auto'
											}
											onClick={() => {
												if (
													edit ||
													!(currentPacket.packet_tag !== 'supervisor' && !isUnassigned)
												) {
													dispatch({
														type: 'update_concern_field',
														payload: {
															section: concernSection,
															concern,
															key: 'no',
															value: !state.fields?.concerns?.[concernSection]?.fields?.[
																concern
															]?.no
														}
													});
												}
											}}
										/>
										<Switch.Root
											colorPalette="blue"
											size="lg"
											borderBottom={`2px solid ${
												currentPacket.packet_tag !== 'supervisor' &&
												state.fields?.concerns?.[concernSection]?.fields?.[concern]?.yes &&
												!state.fields?.concerns?.[concernSection]?.fields?.[concern]?.verified
													? 'red'
													: 'transparent'
											}`}
											onCheckedChange={() =>
												dispatch({
													type: 'update_concern_field',
													payload: {
														section: concernSection,
														concern,
														key: 'verified',
														value: !state.fields?.concerns?.[concernSection]?.fields?.[
															concern
														]?.verified
													}
												})
											}
											checked={
												state.fields?.concerns?.[concernSection]?.fields?.[concern]?.verified
											}
											disabled={
												!edit &&
												(isSubmitted ||
													currentPacket.packet_tag === 'supervisor' ||
													(!state.fields?.concerns?.[concernSection]?.fields?.[concern]
														?.yes &&
														!state.fields?.concerns?.[concernSection]?.fields?.[concern]
															?.no))
											}
										>
											<Switch.HiddenInput />
											<Switch.Control />
										</Switch.Root>
									</Flex>
								</Flex>
							))}
						</Box>
					))}
				</Flex>
			</Flex>
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						If you answered yes to any of the above, please describe the source and type of potential energy
						that you are dealing with:
					</Text>
					<Textarea
						disabled={isSubmitted && !edit}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields.source_type_energy}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'source_type_energy',
									value: target.value
								}
							});
						}}
					/>
				</Box>
			</Flex>
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						How did/are we mitigating or eliminating these risks for today’s task(s)? Have you verified a
						zero-energy state by means of a flowmeter, imaging, etc.? Is the diver’s ingress/egress remote
						from the potential hazard?
					</Text>
					<Textarea
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields.risks_zero_energy_diver_ingress_egress}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'risks_zero_energy_diver_ingress_egress',
									value: target.value
								}
							});
						}}
						disabled={!edit && (isSubmitted || currentPacket.packet_tag === 'supervisor')}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
					/>
				</Box>
			</Flex>
			<Heading fontSize={24} pt="24px" pb="12px" color={theme.colors.blue[700]} ml="8px">
				Signatures
			</Heading>
			<Flex>
				<Box w="100%" p="8px">
					<Text fontSize={16} as="label" pb="4px" pl="8px">
						Project Manager
					</Text>
					<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Text as="label" fontSize={16} pb="4px">
							Print Name
						</Text>
						<Field.Root
							invalid={
								(currentPacket.packet_tag === 'supervisor' || isUnassigned) &&
								!state.fields.project_manager_name
							}
						>
							<Input
								fontSize={16}
								value={state.fields.project_manager_name}
								onChange={({ target }) =>
									dispatch({
										type: 'update_field_value',
										payload: {
											field: 'project_manager_name',
											value: target.value
										}
									})
								}
								disabled={
									!edit &&
									(isSubmitted || (currentPacket.packet_tag !== 'supervisor' && !isUnassigned))
								}
								_disabled={{
									opacity: 1,
									bgColor: theme.colors.gray[400],
									color: theme.colors.black
								}}
								bgColor={theme.colors.white}
								borderRadius="base"
							/>
						</Field.Root>
					</Box>
					<Box
						borderBottom={
							(currentPacket.packet_tag === 'supervisor' || isUnassigned) &&
							!state.fields.project_manager_signature
								? '2px solid red'
								: 'none'
						}
					>
						<SignaturePad
							disabled={
								!edit && (isSubmitted || (currentPacket.packet_tag !== 'supervisor' && !isUnassigned))
							}
							src={
								state.fields.project_manager_signature ||
								'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
							}
							onSign={signatureData =>
								dispatch({
									type: 'save_signature',
									payload: {
										signatureType: 'project_manager',
										signatureData
									}
								})
							}
						/>
					</Box>
				</Box>
				<Box w="100%" p="8px">
					<Text fontSize={16} as="label" pb="4px" pl="8px">
						Supervisor
					</Text>
					<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Text as="label" fontSize={16} pb="4px">
							Print Name
						</Text>
						<Field.Root
							invalid={
								(currentPacket.packet_tag !== 'supervisor' || isUnassigned) &&
								!state.fields.supervisor_name
							}
						>
							<Input
								fontSize={16}
								value={state.fields.supervisor_name}
								onChange={({ target }) =>
									dispatch({
										type: 'update_field_value',
										payload: {
											field: 'supervisor_name',
											value: target.value
										}
									})
								}
								disabled={!edit && (isSubmitted || currentPacket.packet_tag === 'supervisor')}
								_disabled={{
									opacity: 1,
									bgColor: theme.colors.gray[400],
									color: theme.colors.black
								}}
								bgColor={theme.colors.white}
								borderRadius="base"
							/>
						</Field.Root>
					</Box>
					<Box
						borderBottom={
							(currentPacket.packet_tag !== 'supervisor' || isUnassigned) &&
							!state.fields.supervisor_signature
								? '2px solid red'
								: 'none'
						}
					>
						<SignaturePad
							disabled={!edit && (isSubmitted || currentPacket.packet_tag === 'supervisor')}
							src={
								state.fields.supervisor_signature ||
								'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
							}
							onSign={signatureData =>
								dispatch({
									type: 'save_signature',
									payload: {
										signatureType: 'supervisor',
										signatureData
									}
								})
							}
						/>
					</Box>
				</Box>
			</Flex>
		</>
	);
};
